// Elements that should suppress global PRv2 keyboard shortcuts.
const TEXT_ENTRY_TARGETS = [
  'input',
  'textarea',
  'select',
  '[role="textbox"]',
  '[role="combobox"]',
  '[contenteditable="true"]',
  '[data-hotkeys-ignore="true"]',
] as const;

const INTERACTIVE_TARGET_SELECTOR = [
  ...TEXT_ENTRY_TARGETS,
  'button',
  'a',
  '[role="button"]',
  '[role="link"]',
  '[role="menuitem"]',
].join(', ');

const TEXT_ENTRY_TARGET_SELECTOR = TEXT_ENTRY_TARGETS.join(', ');

const FILE_NAVIGATION_HOTKEY_TARGET_SELECTOR = '[data-pr-v2-file-nav-hotkey-target="true"]';
const SIDEBAR_SHORTCUT_CONFLICT_TARGET_SELECTOR = '[data-hotkeys-ignore="true"]';

/**
 * Returns true when the event target is within an interactive control that
 * should not trigger global PRv2 hotkeys (J/K/Space/C, etc.).
 */
export function isPrV2HotkeyInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  const element = target;

  if ((element as HTMLElement).isContentEditable) {
    return true;
  }

  return Boolean(element.closest(INTERACTIVE_TARGET_SELECTOR));
}

/**
 * Returns true when keyboard focus is inside a text-entry surface where
 * printable key combinations should stay local (typing/editing contexts).
 */
export function isPrV2HotkeyTextEntryTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  const element = target;

  if ((element as HTMLElement).isContentEditable) {
    return true;
  }

  return Boolean(element.closest(TEXT_ENTRY_TARGET_SELECTOR));
}

/**
 * Returns true when focus is inside a surface that should keep its own
 * Cmd/Ctrl+I behavior instead of triggering the global PR v2 sidebar hotkey.
 */
export function isPrV2SidebarShortcutConflictTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(target.closest(SIDEBAR_SHORTCUT_CONFLICT_TARGET_SELECTOR));
}

/**
 * Returns true when focus is inside explicit file navigation controls where
 * J/K/Arrow navigation should remain active after mouse clicks.
 */
export function isPrV2FileNavigationHotkeyTarget(target: EventTarget | null): boolean {
  // Use Element (not HTMLElement) so SVG children of file-nav buttons also match.
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(target.closest(FILE_NAVIGATION_HOTKEY_TARGET_SELECTOR));
}
